import React, { useRef } from 'react';
import type { Editor } from '@tiptap/react';
import { useEditorState } from '@tiptap/react';
import Box from '@mui/material/Box';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import ListIcon from '@mui/icons-material/List';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import FormatColorResetIcon from '@mui/icons-material/FormatColorReset';
import { IconButton, Theme } from '@mui/material';

type RichTextEditorMenuProps = {
  editor: Editor;
  theme: Theme;
};

type EditorState = {
  isBold: boolean;
  canBold: boolean;
  isItalic: boolean;
  canItalic: boolean;
  isUnderline: boolean;
  canUnderline: boolean;
  isStrike: boolean;
  canStrike: boolean;
  isBulletList: boolean;
  isOrderedList: boolean;
  color: string;
};

type Button = {
  icon: React.ReactNode;
  click: (editor: Editor) => boolean;
  isDisabledKey?: keyof EditorState;
  isActiveKey?: keyof EditorState;
};

const buttonGroups: Button[][] = [
  [
    {
      icon: <FormatBoldIcon />,
      click: (editor: Editor) => editor.chain().focus().toggleBold().run(),
      isDisabledKey: 'canBold',
      isActiveKey: 'isBold',
    },
    {
      icon: <FormatItalicIcon />,
      click: (editor: Editor) => editor.chain().focus().toggleItalic().run(),
      isDisabledKey: 'canItalic',
      isActiveKey: 'isItalic',
    },
    {
      icon: <FormatUnderlinedIcon />,
      click: (editor: Editor) => editor.chain().focus().toggleUnderline().run(),
      isDisabledKey: 'canUnderline',
      isActiveKey: 'isUnderline',
    },
    {
      icon: <FormatStrikethroughIcon />,
      click: (editor: Editor) => editor.chain().focus().toggleStrike().run(),
      isDisabledKey: 'canStrike',
      isActiveKey: 'isStrike',
    },
  ],
  [
    {
      icon: <ListIcon />,
      click: (editor: Editor) => editor.chain().focus().toggleBulletList().run(),
      isActiveKey: 'isBulletList',
    },
    {
      icon: <FormatListNumberedIcon />,
      click: (editor: Editor) => editor.chain().focus().toggleOrderedList().run(),
      isActiveKey: 'isOrderedList',
    },
  ],
];

function RichTextEditorMenu({ editor, theme }: RichTextEditorMenuProps) {
  const colorInputRef = useRef<HTMLInputElement>(null);

  const editorState = useEditorState<EditorState>({
    editor,
    selector: ({ editor }) => {
      return {
        isBold: editor.isActive('bold') || false,
        canBold: editor.can().chain().toggleBold().run() || false,
        isItalic: editor.isActive('italic') || false,
        canItalic: editor.can().chain().toggleItalic().run() || false,
        isUnderline: editor.isActive('underline') || false,
        canUnderline: editor.can().chain().toggleUnderline().run() || false,
        isStrike: editor.isActive('strike') || false,
        canStrike: editor.can().chain().toggleStrike().run() || false,
        isBulletList: editor.isActive('bulletList') || false,
        isOrderedList: editor.isActive('orderedList') || false,
        color: editor.getAttributes('textStyle').color,
      };
    },
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        backgroundColor: '#F4F4F4',
        marginTop: '0.3rem',
        padding: '0.75rem 0.5rem',
        borderRadius: '5px 5px 0 0',
      }}
    >
      {buttonGroups.map((buttonGroup) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            border: '1px solid #D3D3D3',
            borderRadius: '3px',
            marginRight: '1.5rem',
          }}
        >
          {buttonGroup.map((button, index) => {
            const isActive = button.isActiveKey !== undefined ? editorState[button.isActiveKey] : false;
            return (
              <IconButton
                sx={{
                  borderRadius: '0',
                  padding: '0.2rem',
                  borderLeft: index === 0 ? 'none' : '1px solid #D3D3D3',
                  color: isActive ? theme.palette.primary.main : '#000000',
                }}
                onClick={() => button.click(editor)}
                disabled={button.isDisabledKey !== undefined ? !editorState[button.isDisabledKey] : false}
              >
                {button.icon}
              </IconButton>
            );
          })}
        </Box>
      ))}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          border: '1px solid #D3D3D3',
          borderRadius: '3px',
          marginRight: '1.5rem',
        }}
      >
        <Box>
          <IconButton
            onClick={() => {
              if (colorInputRef !== null && colorInputRef.current !== null) {
                colorInputRef.current.click();
              }
            }}
            sx={{
              borderRadius: '0',
              padding: '0.2rem',
              color: editorState.color,
            }}
          >
            <FormatColorTextIcon />
          </IconButton>
          <input
            ref={colorInputRef}
            type='color'
            onInput={(event) => editor.chain().focus().setColor(event.currentTarget.value).run()}
            onBlur={() => {
              if (colorInputRef !== null && colorInputRef.current !== null) {
                colorInputRef.current.click();
              }
            }}
            value={editorState.color}
            style={{ display: 'none' }}
          />
        </Box>
        <IconButton
          sx={{
            borderRadius: '0',
            padding: '0.2rem',
            borderLeft: '1px solid #D3D3D3',
            color: '#000000',
          }}
          onClick={() => editor.chain().focus().unsetColor().run()}
        >
          <FormatColorResetIcon />
        </IconButton>
      </Box>
    </Box>
  );
}

export default RichTextEditorMenu;
